import type {
  Booking,
  CountryHoliday,
  Mode,
  Plan,
  PlanStop,
  Traveller,
  TravellerLeg,
} from '@trippath/shared';
import { live } from '@trippath/shared';

import { iataOf } from '../../lib/airports';
import { wallOf } from '../../lib/clock';
import { weekendOf } from '../../lib/localCalendar';

/** The country home is: its holidays are those the plan says, its weekend Saturday and Sunday. */
export const HOME = 'EE';

/** Monday 1 to Sunday 7. */
export const weekdayOf = (iso: string) => new Date(`${iso}T00:00:00Z`).getUTCDay() || 7;

const DAY = 86_400_000;

/** `2026-10-18` as a UTC midnight, so adding days never meets a clock change. */
const ms = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const iso = (time: number) => new Date(time).toISOString().slice(0, 10);

/** What one day of the trip holds. */
export interface TripDay {
  iso: string;
  /** 1 for the first day of the trip. */
  tripDay: number;
  /** The stop the night is spent at, with which night of how many; none on the last day. */
  stay: { stop: PlanStop; night: number; nights: number } | null;
  /** The stops left this day, each with the one it goes on to (none after the last stop): more
   * than one when a flight changes planes on the way. */
  legs: { stop: PlanStop; next: PlanStop | null }[];
  /** Ways that arrive this day, having left the day before or earlier: how, when — local
   * there — and where: a flight's airport's code, else the town. */
  arrivals: { stop: PlanStop; mode: Mode; time: string; place: string }[];
  /** Bookings dated this day, each with the stop it is of. */
  bookings: { booking: Booking; stop: PlanStop }[];
  /** Fellow travellers' own legs this day. */
  theirs: { traveller: Traveller; leg: TravellerLeg }[];
  /** Fellow travellers with you this day. */
  together: Traveller[];
  /** The public holiday at home this day falls on: its code. */
  holiday: string | null;
  /** The country the day is spent in: the night's, else the last stop's; home before any. */
  country: string;
  /** The days the country it is spent in keeps on it, public holidays and observances. */
  local: CountryHoliday[];
  /** A day off: the weekend where it is spent, or a public holiday there or at home. */
  off: boolean;
}

/**
 * Every day from the trip's first to its last, by ISO date, and the first of them; `abroad` the
 * days each of its countries keeps, as the Countries page reads them.
 */
export function tripDays(
  plan: Plan,
  abroad: readonly { country: string; holidays: CountryHoliday[] }[] = [],
): { first: TripDay; days: Map<string, TripDay> } {
  const start = ms(plan.trip.start);
  const blank = (time: number): TripDay => ({
    iso: iso(time),
    tripDay: Math.round((time - start) / DAY) + 1,
    stay: null,
    legs: [],
    arrivals: [],
    bookings: [],
    theirs: [],
    together: [],
    holiday: null,
    country: HOME,
    local: [],
    off: false,
  });
  const first = blank(start);
  const days = new Map<string, TripDay>([[first.iso, first]]);
  for (let time = start + DAY; time <= ms(plan.trip.end); time += DAY) {
    days.set(iso(time), blank(time));
  }
  for (const day of days.values()) {
    const stop = plan.stops.find((s) => s.start <= day.iso && day.iso < s.end);
    if (stop) {
      day.stay = {
        stop,
        night: Math.round((ms(day.iso) - ms(stop.start)) / DAY) + 1,
        nights: stop.days,
      };
    }
  }
  plan.stops.forEach((stop, index) => {
    // The day it leaves, local where it leaves: as said, else the day the stop is left.
    const leavingDay = days.get(
      stop.out?.departureAt ? wallOf(stop.out.departureAt, stop.zone).day : stop.end,
    );
    const next = plan.stops[index + 1] ?? null;
    if (
      leavingDay &&
      (stop.out !== null || stop.bookings.some((b) => b.kind === 'ticket' && live(b)))
    ) {
      leavingDay.legs.push({ stop, next });
    }
    // A way that lands on a later day than it left, 23:55 → 00:45: said on the day it lands too.
    const out = stop.out;
    const lands = out?.arrivalAt ? wallOf(out.arrivalAt, next?.zone ?? null) : null;
    if (out && lands && leavingDay && lands.day !== leavingDay.iso) {
      const code = out.mode === 'flight' ? iataOf(out.to?.name) : null;
      days.get(lands.day)?.arrivals.push({
        stop,
        mode: out.mode,
        time: lands.time,
        place: code ?? next?.city ?? '',
      });
    }
    for (const booking of stop.bookings) {
      if (booking.day !== null) days.get(booking.day)?.bookings.push({ booking, stop });
    }
  });
  for (const holiday of plan.holidays) {
    const on = days.get(holiday.day);
    if (on) on.holiday = holiday.code;
  }
  for (const day of days.values()) {
    day.country = plan.stops.findLast((stop) => stop.start <= day.iso)?.country ?? HOME;
    // Home's own days are said as home's already.
    day.local =
      day.country === HOME
        ? []
        : (abroad
            .find((one) => one.country === day.country)
            ?.holidays.filter((holiday) => holiday.day === day.iso) ?? []);
    day.off =
      weekendOf(day.country).includes(weekdayOf(day.iso)) ||
      day.holiday !== null ||
      day.local.some((holiday) => holiday.public);
  }
  for (const traveller of plan.travellers) {
    for (const leg of traveller.legs) days.get(leg.day)?.theirs.push({ traveller, leg });
    for (const day of days.values()) {
      if (traveller.joins <= day.iso && day.iso <= traveller.leaves)
        day.together.push(traveller);
    }
  }
  return { first, days };
}

/**
 * The months the trip touches, first to last, as `[year, month]` with month 0–11: from and to
 * other days when other trips shown go on before or after it.
 */
export function tripMonths(
  plan: Plan,
  last = plan.trip.end,
  first = plan.trip.start,
): [number, number][] {
  const start = new Date(ms(first));
  const end = new Date(ms(last));
  const months: [number, number][] = [];
  for (
    let year = start.getUTCFullYear(), month = start.getUTCMonth();
    year < end.getUTCFullYear() ||
    (year === end.getUTCFullYear() && month <= end.getUTCMonth());
    month = (month + 1) % 12, year += month === 0 ? 1 : 0
  ) {
    months.push([year, month]);
  }
  return months;
}

/** A month's cells, Monday first: `null` for the days before the 1st, then each ISO date. */
export function monthCells(year: number, month: number): (string | null)[] {
  const first = Date.UTC(year, month, 1);
  const lead = (new Date(first).getUTCDay() + 6) % 7;
  const length = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length }, (_, i) => iso(first + i * DAY)),
  ];
}
